import { useState } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, Container, Link, Stack,
  TextField, Typography,
} from '@mui/material';
import { api } from '../api/client';
import { useAuthStore } from '../stores/auth';
import type { LoginResponse } from '../api/types';

export function LoginPage() {
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (token) {
    const dest = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/home';
    navigate(dest, { replace: true });
    return null;
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const { data } = await api.post<LoginResponse>('/api/auth/login', { username, password });
      setSession(data.token, data.user);
      const dest = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname || '/home';
      navigate(dest, { replace: true });
    } catch (ex: unknown) {
      const msg = (ex as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Sign in failed';
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default' }}>
      <Container maxWidth="xs">
        <Card>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>Template.NET</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>Sign in to continue</Typography>
            <Stack component="form" onSubmit={onSubmit} spacing={2}>
              <TextField
                label="Username" value={username} onChange={(e) => setUsername(e.target.value)}
                autoFocus required size="small" autoComplete="username"
              />
              <TextField
                label="Password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} required size="small"
                autoComplete="current-password"
              />
              {err && <Alert severity="error" variant="outlined">{err}</Alert>}
              <Button type="submit" variant="contained" disabled={busy} size="large"
                      startIcon={busy ? <CircularProgress size={18} /> : null}>
                {busy ? 'Signing in...' : 'Sign in'}
              </Button>
              <Stack direction="row" spacing={2} justifyContent="space-between">
                <Link component={RouterLink} to="/forgot-password" variant="body2">
                  Forgot your password?
                </Link>
                <Link component={RouterLink} to="/signup" variant="body2">
                  Create account
                </Link>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
